import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/Work';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import backgroundJobsService, { AllJobsStatus, JobStatus, ProductListCacheStatus } from '../services/backgroundJobsService';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { ConfirmDialog } from '../components/common';
import moment from 'moment-jalaali';

import './EntityManagement.css';

type JobRow = {
  key: string;
  type: 'discount' | 'product-list';
  name: string;
  description: string;
  icon: React.ReactNode;
  status: JobStatus | undefined;
};

const typeLabels: Record<string, string> = {
  discount: 'کش تخفیف‌ها',
  'product-list': 'کش لیست محصولات',
};

const BackgroundJobsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AllJobsStatus | null>(null);
  const [cacheStatus, setCacheStatus] = useState<ProductListCacheStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Action loading states
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsJob, setDetailsJob] = useState<JobRow | null>(null);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    key: string;
    title: string;
    description: string;
    handler: () => Promise<any>;
    variant: 'primary' | 'danger' | 'warning';
  } | null>(null);

  // Form inputs for targeted updates
  const [productId, setProductId] = useState('');
  const [storeId, setStoreId] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusData, cacheData] = await Promise.all([
        backgroundJobsService.getStatus(),
        backgroundJobsService.getProductListCacheStatus(),
      ]);
      setStatus(statusData);
      setCacheStatus(cacheData);
    } catch (err) {
      setError('خطا در بارگذاری وضعیت');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleAction = async (actionKey: string, handler: () => Promise<any>) => {
    setActionLoading(actionKey);
    setError(null);
    try {
      const result = await handler();
      setSuccess(result?.message || 'عملیات با موفقیت انجام شد');
      await loadStatus();
    } catch (err) {
      setError('خطا در اجرای عملیات');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmAction = (
    key: string,
    title: string,
    description: string,
    handler: () => Promise<any>,
    variant: 'primary' | 'danger' | 'warning' = 'primary'
  ) => {
    setPendingAction({ key, title, description, handler, variant });
    setActionDialogOpen(true);
  };

  const executePendingAction = async () => {
    if (!pendingAction) return;
    const { key, handler } = pendingAction;
    setActionDialogOpen(false);
    await handleAction(key, handler);
    setPendingAction(null);
  };

  const cancelPendingAction = () => {
    setActionDialogOpen(false);
    setPendingAction(null);
  };

  const openDetails = (row: JobRow) => {
    setDetailsJob(row);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsJob(null);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setStatusFilter('');
    setTypeFilter('');
    setPage(1);
  };

  // Build rows from status data
  const rows: JobRow[] = [
    {
      key: 'discountCache',
      type: 'discount',
      name: 'کش تخفیف‌ها',
      description: 'محاسبه و بروزرسانی قیمت‌های تخفیف خورده محصولات',
      icon: <LocalOfferIcon />,
      status: status?.discountCache,
    },
    {
      key: 'productListCache',
      type: 'product-list',
      name: 'کش لیست محصولات',
      description: 'بروزرسانی کش لیست‌های محصولات نمایشی',
      icon: <Inventory2Icon />,
      status: status?.productListCache,
    },
  ];

  // Apply filters
  const getRowStatusKey = (row: JobRow): string => {
    if (row.status?.isRunning) return 'running';
    if (row.status?.lastResult === 'success') return 'completed';
    if (row.status?.lastResult === 'error') return 'failed';
    return 'idle';
  };

  const filtered = rows.filter((r) => {
    if (searchTerm && !r.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (typeFilter && r.type !== typeFilter) return false;
    if (statusFilter && getRowStatusKey(r) !== statusFilter) return false;
    return true;
  });

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const hasActiveFilters = !!(searchTerm || statusFilter || typeFilter);

  // Stats
  const totalJobs = rows.length;
  const runningJobs = rows.filter((r) => r.status?.isRunning).length;
  const completedJobs = rows.filter((r) => r.status?.lastResult === 'success').length;
  const failedJobs = rows.filter((r) => r.status?.lastResult === 'error').length;

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    try {
      return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD HH:mm'));
    } catch {
      return '-';
    }
  };

  const renderStatusTag = (row: JobRow) => {
    if (row.status?.isRunning) {
      return (
        <span className="tag tag-sm tag-platform">
          <AutorenewIcon fontSize="inherit" style={{ marginLeft: 2 }} />
          در حال اجرا
        </span>
      );
    }
    const result = row.status?.lastResult;
    if (result === 'success') {
      return <span className="tag tag-sm tag-active">موفق</span>;
    }
    if (result === 'error') {
      return <span className="tag tag-sm tag-inactive">خطا</span>;
    }
    return (
      <span className="tag tag-sm" style={{ background: '#fff3cd', color: '#856404' }}>
        در انتظار
      </span>
    );
  };

  return (
    <div className="entity-page" dir="rtl">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت Jobهای پس‌زمینه</h1>
          <p className="entity-subtitle">مشاهده و مدیریت کارهای در حال اجرا و زمان‌بندی شده</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={loadStatus} disabled={loading} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Banners */}
      {error && (
        <div className="dialog-error" style={{ marginBottom: 12 }}>
          {error}
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', float: 'left' }}
            onClick={() => setError(null)}
          >
            ✕
          </button>
        </div>
      )}
      {success && (
        <div
          className="dialog-error"
          style={{ marginBottom: 12, background: '#dcfce7', borderColor: '#86efac', color: '#15803d' }}
        >
          {success}
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', float: 'left' }}
            onClick={() => setSuccess(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple"><WorkIcon /></div>
          <div>
            <p className="entity-stat-label">کل Job ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(totalJobs))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><AutorenewIcon /></div>
          <div>
            <p className="entity-stat-label">در حال اجرا</p>
            <p className="entity-stat-value">{toPersianDigits(String(runningJobs))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">موفق</p>
            <p className="entity-stat-value">{toPersianDigits(String(completedJobs))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><ErrorOutlineIcon /></div>
          <div>
            <p className="entity-stat-label">ناموفق</p>
            <p className="entity-stat-value">{toPersianDigits(String(failedJobs))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی نام Job..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="running">در حال اجرا</option>
              <option value="completed">موفق</option>
              <option value="failed">ناموفق</option>
              <option value="idle">متوقف / در انتظار</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع</label>
            <select
              className="filter-field"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            >
              <option value="">همه انواع</option>
              <option value="discount">کش تخفیف‌ها</option>
              <option value="product-list">کش لیست محصولات</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {statusFilter && (
              <span className="filter-tag">
                وضعیت: {statusFilter === 'running' ? 'در حال اجرا' : statusFilter === 'completed' ? 'موفق' : statusFilter === 'failed' ? 'ناموفق' : 'متوقف'}
                <button onClick={() => setStatusFilter('')}>×</button>
              </span>
            )}
            {typeFilter && (
              <span className="filter-tag">
                نوع: {typeLabels[typeFilter]}
                <button onClick={() => setTypeFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : paginated.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><WorkIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">Job ای یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>نام Job</th>
                  <th>نوع</th>
                  <th>وضعیت</th>
                  <th>آخرین اجرا</th>
                  <th>پیام</th>
                  <th style={{ width: 150 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">{row.icon}</div>
                        <div className="entity-info">
                          <span className="entity-name">{row.name}</span>
                          <span className="entity-desc" style={{ fontSize: 11, color: '#64748b' }}>
                            {row.description}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tag tag-sm tag-platform">{typeLabels[row.type]}</span>
                    </td>
                    <td>{renderStatusTag(row)}</td>
                    <td>
                      <span className="cell-code">{formatDate(row.status?.lastRun)}</span>
                    </td>
                    <td>
                      <span style={{ fontSize: 12, color: '#475569' }}>
                        {row.status?.message || '-'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => openDetails(row)}>
                            <InfoOutlinedIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="اجرای مجدد" arrow>
                          <button
                            className="action-btn edit"
                            disabled={actionLoading !== null || row.status?.isRunning}
                            onClick={() =>
                              confirmAction(
                                `${row.key}-trigger`,
                                `اجرای ${row.name}`,
                                `آیا از اجرای دستی «${row.name}» اطمینان دارید؟`,
                                row.type === 'discount'
                                  ? backgroundJobsService.triggerDiscountCacheUpdate
                                  : backgroundJobsService.triggerProductListCacheUpdate,
                                'primary'
                              )
                            }
                          >
                            {actionLoading === `${row.key}-trigger` ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PlayArrowIcon />
                            )}
                          </button>
                        </Tooltip>
                        {row.type === 'discount' ? (
                          <Tooltip title="پاکسازی منقضی‌ها" arrow>
                            <button
                              className="action-btn delete"
                              disabled={actionLoading !== null}
                              onClick={() =>
                                confirmAction(
                                  'discount-cleanup',
                                  'پاکسازی تخفیف‌های منقضی',
                                  'آیا از پاکسازی تخفیف‌های منقضی شده اطمینان دارید؟',
                                  backgroundJobsService.cleanExpiredDiscounts,
                                  'warning'
                                )
                              }
                            >
                              <CleaningServicesIcon />
                            </button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="پاک کردن کش" arrow>
                            <button
                              className="action-btn delete"
                              disabled={actionLoading !== null}
                              onClick={() =>
                                confirmAction(
                                  'product-list-invalidate',
                                  'پاک کردن کش لیست محصولات',
                                  'آیا از پاک کردن کامل کش لیست محصولات اطمینان دارید؟ این عملیات باعث ایجاد بار موقت روی سرور خواهد شد.',
                                  backgroundJobsService.invalidateProductListCache,
                                  'danger'
                                )
                              }
                            >
                              <DeleteSweepIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {paginated.map((row) => (
                <div key={row.key} className={`entity-card ${row.status?.lastResult === 'error' ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">{row.icon}</div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{row.name}</div>
                      <div className="entity-card-desc">{row.description}</div>
                      <div className="entity-card-desc" style={{ marginTop: 4 }}>
                        آخرین اجرا: {formatDate(row.status?.lastRun)}
                      </div>
                    </div>
                    <div className="entity-card-status">{renderStatusTag(row)}</div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDetails(row)}>
                      <InfoOutlinedIcon fontSize="small" /> جزئیات
                    </button>
                    <button
                      className="entity-card-btn primary"
                      disabled={actionLoading !== null || row.status?.isRunning}
                      onClick={() =>
                        confirmAction(
                          `${row.key}-trigger`,
                          `اجرای ${row.name}`,
                          `آیا از اجرای دستی «${row.name}» اطمینان دارید؟`,
                          row.type === 'discount'
                            ? backgroundJobsService.triggerDiscountCacheUpdate
                            : backgroundJobsService.triggerProductListCacheUpdate,
                          'primary'
                        )
                      }
                    >
                      <PlayArrowIcon fontSize="small" /> اجرا
                    </button>
                    <button
                      className="entity-card-btn danger"
                      disabled={actionLoading !== null}
                      onClick={() =>
                        row.type === 'discount'
                          ? confirmAction(
                              'discount-cleanup',
                              'پاکسازی تخفیف‌های منقضی',
                              'آیا از پاکسازی تخفیف‌های منقضی شده اطمینان دارید؟',
                              backgroundJobsService.cleanExpiredDiscounts,
                              'warning'
                            )
                          : confirmAction(
                              'product-list-invalidate',
                              'پاک کردن کش لیست محصولات',
                              'آیا از پاک کردن کامل کش لیست محصولات اطمینان دارید؟',
                              backgroundJobsService.invalidateProductListCache,
                              'danger'
                            )
                      }
                    >
                      {row.type === 'discount' ? (
                        <><CleaningServicesIcon fontSize="small" /> پاکسازی</>
                      ) : (
                        <><DeleteSweepIcon fontSize="small" /> پاک کردن</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalCount))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  بعدی
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(String(page))} / {toPersianDigits(String(totalPages))}
                </span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  قبلی
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth className="job-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات {detailsJob?.name}</span>
          <button className="details-close" onClick={closeDetails}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {detailsJob && (
            <div className="edit-form">
              {/* General */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات کلی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام Job</label>
                      <div style={{ fontWeight: 600 }}>{detailsJob.name}</div>
                    </div>
                    <div className="edit-field">
                      <label>نوع</label>
                      <div>
                        <span className="tag tag-sm tag-platform">{typeLabels[detailsJob.type]}</span>
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>وضعیت فعلی</label>
                      <div>{renderStatusTag(detailsJob)}</div>
                    </div>
                    <div className="edit-field">
                      <label>آخرین اجرا</label>
                      <div className="cell-code">{formatDate(detailsJob.status?.lastRun)}</div>
                    </div>
                    <div className="edit-field full">
                      <label>پیام آخرین اجرا</label>
                      <div>{detailsJob.status?.message || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product List Cache extra info */}
              {detailsJob.type === 'product-list' && cacheStatus && (
                <div className="edit-card">
                  <div className="edit-card-header">وضعیت کش</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>فعال</label>
                        <div>
                          <span className={`tag tag-sm ${cacheStatus.isEnabled ? 'tag-active' : 'tag-inactive'}`}>
                            {cacheStatus.isEnabled ? 'فعال' : 'غیرفعال'}
                          </span>
                        </div>
                      </div>
                      <div className="edit-field">
                        <label>حجم کش</label>
                        <div>{toPersianDigits(String(cacheStatus.cacheSize || 0))}</div>
                      </div>
                      {cacheStatus.hitRate !== undefined && (
                        <div className="edit-field">
                          <label>نرخ برخورد</label>
                          <div>{toPersianDigits((cacheStatus.hitRate * 100).toFixed(1))}%</div>
                        </div>
                      )}
                      {cacheStatus.lastUpdate && (
                        <div className="edit-field">
                          <label>آخرین بروزرسانی</label>
                          <div className="cell-code">{formatDate(cacheStatus.lastUpdate)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Targeted update for discount cache */}
              {detailsJob.type === 'discount' && (
                <div className="edit-card">
                  <div className="edit-card-header">بروزرسانی محصول/فروشگاه خاص</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>شناسه محصول</label>
                        <input
                          type="text"
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          placeholder="شناسه محصول"
                        />
                      </div>
                      <div className="edit-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          className="edit-btn submit"
                          style={{ width: '100%' }}
                          disabled={!productId || actionLoading !== null}
                          onClick={() =>
                            handleAction('product', () => backgroundJobsService.updateProductDiscount(productId))
                          }
                        >
                          {actionLoading === 'product' && <CircularProgress size={14} style={{ color: '#fff' }} />}
                          بروزرسانی محصول
                        </button>
                      </div>
                      <div className="edit-field">
                        <label>شناسه فروشگاه</label>
                        <input
                          type="text"
                          value={storeId}
                          onChange={(e) => setStoreId(e.target.value)}
                          placeholder="شناسه فروشگاه"
                        />
                      </div>
                      <div className="edit-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          className="edit-btn submit"
                          style={{ width: '100%' }}
                          disabled={!storeId || actionLoading !== null}
                          onClick={() =>
                            handleAction('store', () => backgroundJobsService.updateStoreDiscounts(storeId))
                          }
                        >
                          {actionLoading === 'store' && <CircularProgress size={14} style={{ color: '#fff' }} />}
                          بروزرسانی فروشگاه
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDetails}>بستن</button>
        </div>
      </Dialog>

      {/* Action Confirm Dialog */}
      <ConfirmDialog
        open={actionDialogOpen}
        title={pendingAction?.title || 'تأیید عملیات'}
        message={pendingAction?.description || 'آیا از انجام این عملیات اطمینان دارید؟'}
        cancelText="انصراف"
        confirmText="تأیید و اجرا"
        confirmColor={
          pendingAction?.variant === 'danger'
            ? 'error'
            : pendingAction?.variant === 'warning'
            ? 'warning'
            : 'primary'
        }
        loading={actionLoading !== null}
        onCancel={cancelPendingAction}
        onConfirm={executePendingAction}
      />
    </div>
  );
};

export default BackgroundJobsPage;
